import React from 'react';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Auth0Provider } from '@auth0/auth0-react';
import type { AppState } from '@auth0/auth0-react';
import '@fontsource-variable/manrope';
import { AuthApolloProvider } from '~/apollo';
import App from '~/App.tsx';
import { globalStyles } from '~/shared/styles/global';
import { theme } from '~/shared/styles/theme';
import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  SENTRY_DSN,
} from '~/shared/config';
import { IS_DEV, IS_TEST, LocalStorageKeys } from '~/shared/constants';

interface InvitationAppState {
  invitationToken: string;
  invitationDecision: 'accept' | 'decline';
}

const isInvitationAppState = (
  appState: AppState,
): appState is InvitationAppState => {
  return (
    typeof appState === 'object' &&
    appState !== null &&
    'invitationToken' in appState &&
    'invitationDecision' in appState &&
    typeof appState.invitationToken === 'string' &&
    ['accept', 'decline'].includes(appState.invitationDecision)
  );
};

const enableMocking = async () => {
  if (!IS_TEST) {
    return;
  }

  const { worker } = await import('~/tests/mocks/browser');

  return worker.start();
};

if (!IS_DEV && !IS_TEST && SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: false,
  });
}

const handleRedirectCallback = (appState?: AppState) => {
  if (appState && isInvitationAppState(appState)) {
    localStorage.setItem(
      LocalStorageKeys.InvitationToken,
      appState.invitationToken,
    );
    localStorage.setItem(
      LocalStorageKeys.InvitationDecision,
      appState.invitationDecision,
    );
  }
};

enableMocking().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Auth0Provider
        domain={AUTH0_DOMAIN}
        clientId={AUTH0_CLIENT_ID}
        useRefreshTokens={true}
        cacheLocation="localstorage"
        onRedirectCallback={handleRedirectCallback}
        authorizationParams={{
          redirect_uri: `${window.location.origin}/auth-callback`,
          audience: AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access',
        }}
      >
        <AuthApolloProvider>
          <ThemeProvider theme={theme}>
            <GlobalStyles styles={globalStyles} />
            <CssBaseline enableColorScheme />
            <App />
          </ThemeProvider>
        </AuthApolloProvider>
      </Auth0Provider>
    </React.StrictMode>,
  );
});
